import { useState } from "react";
import { Tabs } from "..";
import { Icon } from "../../../../components/icon/icon";
import { SwitchInput } from "../../../../components/inputs/switch";
import { DiscountModal } from "../../../../components/modal/app_modals/discountModal";
import { Pill } from "../../../../components/pill";
import { TableComponent } from "../../../../components/table";
import { MiniHeader } from "../../../../components/typography/miniHeader";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../../customeHooks/useModalControl";
import {
  useGetDiscountsQuery,
  useToggleAssignEventDiscountMutation,
} from "../../../../redux/features/event/event.api";
import { CreatedEvent } from "../../../../redux/features/event/event.types";
import { formatDate, formatToNaira } from "../../../../utils/helpers";
import { QueryParams } from "../../../../utils/types";

export const EventSettings = ({
  event,
  setTab,
  refetchEvent = () => {},
}: {
  event: CreatedEvent;
  setTab: (tab: Tabs) => void;
  refetchEvent: () => void;
}) => {
  const [queryParams, setQueryParams] = useState<
    QueryParams & {
      status?: string;
    }
  >({
    page: 1,
    limit: 20,
    search: undefined,
    sortOrder: "asc",
    status: undefined,
  });

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const {
    data: disounts,
    isFetching: fetchingDiscounts,
    refetch: refetchDiscounts,
  } = useGetDiscountsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const [toggleAssignEventDiscount, { isLoading: assigningEventDiscount }] =
    useToggleAssignEventDiscountMutation();

  return (
    <>
      <BgWrapper className="flex flex-col gap-3 !rounded-t-none">
        <MiniHeader header="Event Reminders" />

        <TableComponent
          headers={[]}
          rows={[
            {
              id: 1,
              items: [
                <div className="flex items-start gap-4">
                  <Icon
                    className="min-w-fit"
                    svg="alarm-clock"
                    width={20}
                    height={20}
                  />

                  <div className="flex-1 text-grey-900">
                    <p className="text-base font-bold">
                      1 Day Before Event Reminder
                    </p>
                    <p className="text-sm">
                      10:00 AM -{" "}
                      {formatDate(event.startDate, "ddd, MMMM DD, YYYY")}
                    </p>
                  </div>

                  <SwitchInput
                    checked={true}
                    onChange={() => {}}
                    disabled={true}
                    className="self-center"
                  />
                </div>,
              ],
            },
            {
              id: 2,
              items: [
                <div className="flex items-start gap-4">
                  <Icon
                    className="min-w-fit"
                    svg="alarm-clock"
                    width={20}
                    height={20}
                  />

                  <div className="flex-1 text-grey-900">
                    <p className="text-base font-bold">Event Day Reminder</p>
                    <p className="text-sm">
                      8:00 AM -{" "}
                      {formatDate(event.startDate, "ddd, MMMM DD, YYYY")}
                    </p>
                  </div>

                  <SwitchInput
                    checked={true}
                    onChange={() => {}}
                    disabled={true}
                    className="self-center"
                  />
                </div>,
              ],
            },
          ]}
        />
      </BgWrapper>

      <BgWrapper className="flex flex-col gap-3">
        <MiniHeader header="Discounts" />

        <TableComponent
          headers={[]}
          rows={
            disounts?.data?.data?.map((discount) => {
              return {
                id: discount._id,
                items: [
                  <div className="flex items-center gap-5" key={discount._id}>
                    <Icon
                      svg="circle-percent"
                      width={24}
                      height={24}
                      className="hidden md:flex"
                    />

                    <div className="flex-1">
                      <MiniHeader
                        header={`${discount.name.toUpperCase()}`}
                        subheader={`${discount.type === "percentage" ? `${discount.value === 100 ? "FREE" : `${discount.value}% Discount`}` : `${formatToNaira(discount.value)} Discount`} ∙ ${discount.quantityType === "limited" ? `${discount.usageLimit}/${discount.quantity} Used` : "Unlimited"}`}
                        className="uppercase text-grey-900"
                      />
                    </div>

                    <Pill
                      text={discount.isActive ? "ACTIVE" : "INACTIVE"}
                      variation={discount.isActive ? "green" : "grey"}
                    />

                    <SwitchInput
                      checked={(
                        event?.discounts?.map((d) => d as unknown as string) ??
                        []
                      ).includes(discount._id ?? "")}
                      onChange={async (checked) => {
                        const resp = await toggleAssignEventDiscount({
                          eventId: event._id,
                          discountCode: discount.name ?? "",
                          discountCreatedBy: discount.createdBy._id,
                          status: checked ? "unassign" : "assign",
                        });

                        if (resp.data?.status === "success") {
                          refetchEvent();
                        }
                      }}
                      disabled={!discount.isActive || assigningEventDiscount}
                      className="self-center"
                    />
                  </div>,
                ],
              };
            }) ?? []
          }
          isRowClickable={false}
          isTableEmpty={[undefined, 0].includes(disounts?.data?.data?.length)}
          emptyTitle="No Discount Added"
          emptySubTitle="Reduce ticket prices with discounts"
          emptyImage="/assets/images/percent-glass.png"
          ctaIcon="plus"
          isLoading={fetchingDiscounts}
          loadingRows={4}
          ctaText="Add Discount"
          onCTAClick={() => {
            handleModalOpen("discountModal");
          }}
          isPaginated
          currentPage={queryParams.page!}
          totalCount={disounts?.data?.totalCount ?? 0}
          itemsPerPage={queryParams.limit!}
          onPageChange={(page) => {
            setQueryParams({
              ...queryParams,
              page: page,
            });
          }}
        />
      </BgWrapper>

      {/* <BgWrapper className="flex flex-col gap-3">
        <MiniHeader header="More Options" />

        <button
          className="soft-shrink flex items-start gap-4 border-b border-grey-200 p-3"
          type="button"
          onClick={() => {}}
        >
          <Icon className="min-w-fit" svg="copy-plus" width={20} height={20} />

          <p className="flex flex-1 flex-col text-left text-grey-900">
            <span className="text-base font-bold">Duplicate Event</span>
            <span className="text-sm">
              Create a copy of this event with all the event details
            </span>
          </p>

          <Icon
            className="min-w-fit -rotate-90 self-center"
            svg="caret-down"
            width={20}
            height={20}
          />
        </button>

        <button
          className="soft-shrink flex items-start gap-4 p-3"
          type="button"
          onClick={() => {}}
        >
          <Icon
            className="min-w-fit text-danger"
            svg="bin"
            width={20}
            height={20}
          />

          <p className="flex flex-1 flex-col text-left text-grey-900">
            <span className="text-base font-bold text-danger">
              Delete Event
            </span>
            <span className="text-sm">
              Delete this event and all the details permanently
            </span>
          </p>

          <Icon
            className="min-w-fit -rotate-90 self-center"
            svg="caret-down"
            width={20}
            height={20}
          />
        </button>
      </BgWrapper> */}

      <DiscountModal
        open={modalOpen && modal === "discountModal"}
        handleClose={() => {
          handleModalClose();
        }}
        onSuccess={() => {
          handleModalClose();
          refetchDiscounts();
        }}
        mode="create"
      />
    </>
  );
};
